import { Injectable } from "@angular/core";
import { BACKEND_URI } from "../config/config-vars";
import { HttpClient } from "@angular/common/http";
import { GameService } from "./game.service";
import { LoginService } from "./login.service";
import { Board } from "../models/board.model";
import { firstValueFrom, map } from "rxjs";

const BASE_URL = `${BACKEND_URI}/api/board`;
const GET_ALL_URL = BASE_URL;
const GET_PAGE_URL = BASE_URL;
const INSERT_URL = BASE_URL;
const UPLOAD_FILES_URL = `${BASE_URL}/file/upsert`;
const SET_ENABLED_URL = (id) => `${BASE_URL}/${id}/enable-update`;
const SET_FUSION_CONNECT_URL = (id: string) => `${BASE_URL}/${id}/fusion-connect-update`;

const UPDATE_URL = (id) => `${BASE_URL}/${id}`;
const GET_BY_ID_URL = (id) => `${BASE_URL}/${id}`;
const DELETE_BY_ID_URL = (id) => `${BASE_URL}/${id}`;
const DOWNLOAD_FILES_URL = `${BASE_URL}/file/download`;
const COUNT_CONTRACTED_BOARDS_URL = `${BASE_URL}/stats/countContracted`;
const COUNT_CONTRACTED_BOARDS_WAREHOUSE_URL = `${BASE_URL}/stats/countContractedWarehouse`;



interface FileUploadDetail {
    fileName: string;
    file: ArrayBuffer;
}

@Injectable({providedIn: 'root'})
export class BoardService {

 

    constructor(private http: HttpClient, private loginService: LoginService){}

    async getAll(): Promise<Board[]> {
        if (!this.loginService.isloggedIn) {
            return [];
        }

        const pageSize = 500;
        let page = 0;
        const results: Board[] = [];

        while (true) {
            const pageData = await this.getPage(page, pageSize);
            if (!pageData?.length) {
                break;
            }

            results.push(...pageData);
            if (pageData.length < pageSize) {
                break;
            }
            page += 1;
        }

        return results;
    }

    async getPage(page: number = 0, pageSize: number = 100): Promise<Board[]> {
        if (this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.get<Board[]>(GET_PAGE_URL, {
                    params: { page: page.toString(), pageSize: pageSize.toString() }
                })
            );
        }
        return [];
    }

    async setEnabled(id: string, enabled: boolean): Promise<Board> {
        if(this.loginService.isloggedIn) {
            console.trace(`setEnabled(${enabled}): user is logged in`)
            return await firstValueFrom(
                this.http.patch<Board>(SET_ENABLED_URL(id), {enabled})
            );
        }else {
            console.error(`setEnabled(${enabled}): not logged in`)
        }
    }
    async setFusionConnect(boardId: string, fusionConnect: boolean): Promise<Board>  {
        if (this.loginService.isloggedIn) {
          console.log(`Updating board ${boardId} with fusionConnect = ${fusionConnect}`);
      
          return await firstValueFrom(
            this.http.patch<Board>(SET_FUSION_CONNECT_URL(boardId), { fusionConnect })
          );
        } else {
          console.error(`setFusionConnect(${fusionConnect}): not logged in`);
          return null;
        }
      }
      
      
      
      

    async getById(id): Promise<Board> {
        if(this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.get<Board>(GET_BY_ID_URL(id))
            );
        }
    }
    async create(data: Board): Promise<Board> {
        if(this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.post<Board>(INSERT_URL, data)
            );
        }
    }
    async update(id, data: Board): Promise<Board> {
        if(this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.put<Board>(UPDATE_URL(id), data)
            );
        }
    }

    async delete(id): Promise<void> {
        if(this.loginService.isloggedIn) {
            await firstValueFrom(
                this.http.delete(DELETE_BY_ID_URL(id))
            );
        }
    }

    async countContractedBoards(): Promise<number> {
        if(this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.get<number>(COUNT_CONTRACTED_BOARDS_URL)
            )
        }
    }

    async countContractedBoardsWarehouse(): Promise<number> {
        if(this.loginService.isloggedIn) {
            return await firstValueFrom(
                this.http.get<number>(COUNT_CONTRACTED_BOARDS_WAREHOUSE_URL)
            )
        }
    }

    async uploadFiles(files: File[]) {
      if(this.loginService.isloggedIn) {
        const formData = new FormData();
        for(const f of files) {
            formData.append('files', f);
        }
        await firstValueFrom(
              this.http.post(UPLOAD_FILES_URL, formData)
        );
      }
    }

    async download(): Promise<FileUploadDetail> {
        if(this.loginService.isloggedIn) {
            console.log("BoardService.download()")
            return await firstValueFrom(
                this.http.get(DOWNLOAD_FILES_URL, {
                    headers: {
                    },
                    observe: "response",
                    responseType: "arraybuffer"
                }).pipe(
                    map(response => {
                        const fileName = response.headers.get("Content-Disposition")
                            ?.split(';')[1].split('=')[1];
                        const file = response.body;
                        return {
                            fileName, file
                        };
                    })
                )
            );
        }
      }
      async getBoardsBySalesRepId(salesRepId: string): Promise<Board[]> {
  if (!salesRepId) return [];

  // If you DO have a backend filter endpoint, you can use it here instead:
  // try {
  //   const res = await firstValueFrom(
  //     this.http.get<Board[]>(`${this.baseUrl}/boards`, { params: { salesRepId } })
  //   );
  //   return res ?? [];
  // } catch {
  //   // fall back to client-side filtering below
  // }

  // Fallback: fetch all boards and filter locally
  const all = await this.getAll();               // assumes you already have getAll(): Promise<Board[]>
  return (all ?? []).filter((b: any) => {
    const repIdOnBoard =
      b?.salesRepId ||
      b?.salesRep?.id ||
      b?.location?.salesRep?.id ||
      b?.currentDeployment?.location?.salesRep?.id ||
      b?.currentDeployment?.salesRepId;

    return repIdOnBoard === salesRepId;
  });
}

      
}
